import React, { useCallback, useEffect, useState } from "react";
import { TrendingUp, Package, Music4, Coins, RotateCcw, Wine } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, formatError } from "../../lib/api";
import { AdminHeader } from "../../components/AdminLayout";
import { useAuth } from "../../context/AuthContext";

const Panel = ({ icon: Icon, title, children }) => (
  <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
    <div className="flex items-center gap-2 mb-4">
      <Icon size={18} className="text-[#D4AF37]" />
      <h3 className="font-display text-lg font-semibold">{title}</h3>
    </div>
    {children}
  </div>
);

const Bars = ({ data, valueKey = "value", prefix = "" }) => {
  const max = Math.max(1, ...data.map((d) => d[valueKey]));
  if (data.length === 0) return <p className="text-[#A19DB0] text-sm">No data yet.</p>;
  return (
    <div className="space-y-2.5">
      {data.slice(-10).map((d) => (
        <div key={d.date || d.name} className="flex items-center gap-3">
          <span className="text-xs text-[#A19DB0] w-16 shrink-0">{d.date ? fmtDate(d.date) : d.name}</span>
          <div className="flex-1 h-2.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-[#D4AF37] rounded-full" style={{ width: `${(d[valueKey] / max) * 100}%` }} />
          </div>
          <span className="text-xs font-medium w-16 text-right">{prefix}{d[valueKey]}</span>
        </div>
      ))}
    </div>
  );
};

export default function AdminReports() {
  const { user } = useAuth();
  const [rep, setRep] = useState(null);
  const [drinks, setDrinks] = useState(null);
  const load = useCallback(() => api.get("/admin/reports").then((r) => setRep(r.data)).catch(() => setRep({})), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/drinks-report").then((r) => setDrinks(r.data)).catch(() => setDrinks({})); }, []);
  const r = rep || {};

  const resetPL = async () => {
    if (!window.confirm("Reset all revenue & stats shown on Reports and the Dashboard to zero from now?\n\nThis does NOT delete any orders, passes, members or bookings — it only clears the displayed totals.")) return;
    try { await api.post("/admin/reset-pl"); toast.success("P/L reset — reports now start from zero"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <AdminHeader title="Reports" subtitle="Revenue, sales and usage"
        action={user.role === "super_admin" ? (
          <button onClick={resetPL} data-testid="reset-pl-btn" className="flex items-center gap-2 bg-[#E57373]/15 text-[#E57373] border border-[#E57373]/30 rounded-full px-4 py-2.5 text-sm font-semibold active:scale-95 transition-transform"><RotateCcw size={15} /> Reset P/L</button>
        ) : null} />
      <div className="grid lg:grid-cols-2 gap-4">
        <Panel icon={TrendingUp} title="Revenue by day (S$)">
          <Bars data={r.revenue_by_day || []} prefix="S$" />
        </Panel>
        <Panel icon={Coins} title="Pass usage by day">
          <Bars data={r.credit_usage_by_day || []} />
        </Panel>
        <Panel icon={Package} title="Membership sales">
          {(r.sales_by_package || []).length === 0 ? <p className="text-[#A19DB0] text-sm">No sales yet.</p> : (
            <div className="space-y-2">
              {r.sales_by_package.map((s) => (
                <div key={s.name} className="flex items-center justify-between text-sm">
                  <span>{s.name}</span>
                  <span className="text-[#A19DB0]">{s.count} sold · <span className="text-[#D4AF37]">S${s.revenue}</span></span>
                </div>
              ))}
            </div>
          )}
        </Panel>
        <Panel icon={Music4} title="Popular classes">
          {(r.popular_classes || []).length === 0 ? <p className="text-[#A19DB0] text-sm">No bookings yet.</p> : (
            <div className="space-y-2">
              {r.popular_classes.map((c) => (
                <div key={c.name} className="flex items-center justify-between text-sm">
                  <span>{c.name}</span>
                  <span className="text-[#D4AF37]">{c.bookings} bookings</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
        <Panel icon={Wine} title={`Drinks redeemed by day${drinks?.total ? ` · ${drinks.total} total` : ""}`}>
          <Bars data={drinks?.by_day || []} />
        </Panel>
        <Panel icon={Wine} title="Drinks redeemed by event">
          <Bars data={drinks?.by_event || []} />
        </Panel>
      </div>
    </div>
  );
}
